import React, { useState, useCallback, useEffect, useRef } from "react";
import { MdCheck, MdClose } from "react-icons/md";
import { useTranslation } from "react-i18next";

interface Option {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  selectedValues: string[];
  onChange: (selectedValues: string[]) => void;
  placeholder?: string;
  className?: string;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedValues,
  onChange,
  placeholder,
  className = ""
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const resolvedPlaceholder = placeholder || t("selection.select");

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Filter options based on search term
  const filteredOptions = searchTerm
    ? options.filter(option => 
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  // Toggle selection of an option
  const toggleOption = useCallback((value: string) => {
    onChange(
      selectedValues.includes(value)
        ? selectedValues.filter(v => v !== value)
        : [...selectedValues, value]
    );
  }, [selectedValues, onChange]);



  // Remove a selected option
  const removeOption = useCallback((e: React.MouseEvent, value: string) => {
    e.stopPropagation();
    onChange(selectedValues.filter(v => v !== value));
  }, [selectedValues, onChange]);

  // Clear all selected options
  const clearAll = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([]);
  }, [onChange]);

  // Determine if all options are selected
  const allSelected = selectedValues.length === options.length && options.length > 0;

  // Handler to toggle select all / clear all
  const handleSelectAllToggle = useCallback(() => {
    if (allSelected) {
      onChange([]);
    } else {
      onChange(options.map(opt => opt.value));
    }
  }, [allSelected, onChange, options]);

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Selected tags/bubbles */}
      {selectedValues.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {selectedValues.map(value => {
            const option = options.find(opt => opt.value === value);
            return option ? (
              <div
                key={value}
                className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full flex items-center border border-blue-200"
              >
                <span className="truncate max-w-[120px]">{option.label}</span>
                <button
                  onClick={(e) => removeOption(e, value)}
                  className="ml-2 text-blue-600 hover:text-blue-800 flex-shrink-0"
                  aria-label={t("selection.removeOption")}
                >
                  <MdClose size={14} />
                </button>
              </div>
            ) : null;
          })}
          {selectedValues.length > 0 && (
            <button
              onClick={(e) => clearAll(e)}
              className="text-slate-500 hover:text-slate-700 text-sm px-2 py-1 rounded-full border border-slate-300 hover:border-slate-400 transition-colors"
              aria-label={t("selection.clear")}
            >
              {t("selection.clearAll")}
            </button>
          )}
        </div>
      )}

      {/* Dropdown trigger */}
      <div
        className={`w-full px-4 py-3 border rounded-xl cursor-pointer flex justify-between items-center bg-white transition-colors ${
          isOpen ? "ring-2 ring-slate-500 border-transparent" : "border-slate-300 focus:ring-2 focus:ring-slate-500"
        }`}
        onClick={() => setIsOpen(prev => !prev)}
      >
        <span className={`${selectedValues.length === 0 ? "text-slate-500" : "text-slate-900"}`}>
          {selectedValues.length === 0
            ? resolvedPlaceholder
            : t("selection.selected", { count: selectedValues.length })}
        </span>
        <svg className="w-5 h-5 ml-2 flex-shrink-0 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={isOpen ? "M5 15l7-7 7 7" : "M19 9l-7 7-7-7"} />
        </svg>
      </div>

      {/* Dropdown content */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white rounded-xl shadow-sm border border-slate-200 max-h-80 flex flex-col">
          
          {/* Search input */}
          <div className="p-3 border-b border-slate-200 sticky top-0 bg-white z-10">
            <input
              type="text"
              className="w-full px-3 py-2 border border-slate-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent"
              placeholder={t("selection.search")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* Options list */}
          <div className="py-2 overflow-y-auto flex-1">
            {/* Select All / Clear All toggle */}
            {options.length > 0 && (
              <div
                className="px-4 py-3 text-slate-700 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0 flex items-center justify-between transition-colors"
                onClick={handleSelectAllToggle}
              >
                <span className="truncate">
                  {allSelected
                    ? t("selection.deselectAll")
                    : t("selection.selectAll")}
                </span>
                {allSelected && <MdCheck className="text-slate-900 flex-shrink-0" size={18} />}
              </div>
            )}

            {filteredOptions.length > 0 ? (
              filteredOptions.map(option => (
                <div
                  key={option.value}
                  className={`px-4 py-3 flex items-center justify-between hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-b-0 transition-colors ${
                    selectedValues.includes(option.value) ? "bg-slate-50 text-slate-900 font-semibold" : "text-slate-700"
                  }`}
                  onClick={() => toggleOption(option.value)}
                >
                  <span className="truncate">{option.label}</span>
                  {selectedValues.includes(option.value) && (
                    <MdCheck className="text-slate-900 flex-shrink-0" size={18} />
                  )}
                </div>
              ))
            ) : (
              <div className="px-4 py-2 text-gray-500 italic">
                {t("selection.noResults")}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
